import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PRIMARY, cn } from '../constants/theme';
import { PageProps } from '../types';
import { Header } from '../components';
import { Footer } from '../components';

export function HomePage({ dark, onToggleDark }: PageProps) {
  const navigate = useNavigate();

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden">
      <div className="flex grow flex-col">
        <Header
          dark={dark}
          onToggleDark={onToggleDark}
          onGoNotes={() => navigate('/notes')}
        />

        {/* Hero Section */}
        <section className="px-6 md:px-40 flex justify-center py-10 md:py-20">
          <div className="flex flex-col max-w-[960px] flex-1">
            <h1
              className={cn(
                'tracking-tight text-[36px] md:text-[48px] font-bold leading-tight px-4 text-left pb-4',
                dark ? 'text-white' : 'text-slate-900'
              )}
            >
              FATEH OK <span className="font-normal opacity-50">|</span>{' '}
              <span className="font-normal">Software Engineer</span>
            </h1>
            <h2
              className={cn(
                'text-xl md:text-2xl font-medium leading-tight px-4 pb-2',
                dark ? 'text-slate-400' : 'text-slate-600'
              )}
            >
              Engineering Craft with a People Mindset
            </h2>
            <p
              className={cn(
                'text-base md:text-lg px-4 pb-6 mono-text',
                dark ? 'text-slate-500' : 'text-slate-500'
              )}
            >
              Exploring complex systems, orchestration, and small products, in public
            </p>
            <p
              className={cn(
                'text-lg md:text-xl font-normal leading-relaxed max-w-2xl px-4',
                dark ? 'text-slate-300' : 'text-slate-600'
              )}
            >
              I'm a software engineer working on{' '}
              <a
                href="#projects"
                className="mono-text font-medium hover-underline"
                style={{ color: PRIMARY }}
              >
                complex systems
              </a>{' '}
              and learning how teams build and collaborate.
              <br />
              <br />
              Lately, I've been exploring how clarity emerges when product, UX, and
              engineering are shaped together, often through small, focused experiments.
              <br />
              <br />I care about engineering craft, thoughtful problem-solving, and
              working closely with{' '}
              <a
                href="#connect"
                className="mono-text font-medium hover-underline"
                style={{ color: PRIMARY }}
              >
                people
              </a>
              . I value clarity, rigor, and continuous learning, and I enjoy environments
              where curiosity and collaboration are encouraged.
            </p>

            {/* What I'm focused on now */}
            <div className="px-4 pt-8">
              <h3
                className={cn(
                  'text-sm font-semibold uppercase tracking-widest mb-4',
                  dark ? 'text-slate-500' : 'text-slate-400'
                )}
              >
                What I'm focused on now
              </h3>
              <ul
                className={cn(
                  'space-y-2 text-base',
                  dark ? 'text-slate-300' : 'text-slate-600'
                )}
              >
                <li className="flex items-start gap-2">
                  <span style={{ color: PRIMARY }}>→</span>
                  Building small systems to explore orchestration problems
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: PRIMARY }}>→</span>
                  Writing short notes about product{' '}
                  <span style={{ color: PRIMARY }}>×</span> engineering trade-offs
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: PRIMARY }}>→</span>
                  Learning in public through experiments and reflection
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Writing Section */}
        <section className="px-6 md:px-40 flex justify-center py-10">
          <div className="flex flex-col max-w-[960px] flex-1">
            <div className="flex items-center justify-between px-4 pb-6 pt-5">
              <h2
                className={cn(
                  'text-[24px] font-bold leading-tight tracking-tight',
                  dark ? 'text-white' : 'text-slate-900'
                )}
              >
                Writing
              </h2>
              <button
                className="mono-text text-sm font-medium hover-underline"
                style={{ color: PRIMARY }}
                type="button"
                onClick={() => navigate('/notes')}
              >
                view_all_notes()
              </button>
            </div>

            <div className="flex flex-col gap-1">
              {NOTE_POSTS.map((post) => (
                <BlogPostCard
                  key={post.title}
                  title={post.title}
                  date={post.date}
                  dark={dark}
                  onClick={() => navigate('/notes')}
                />
              ))}
            </div>
          </div>
        </section>

        <Footer dark={dark} />
      </div>
    </div>
  );
}

// Blog post preview data
const NOTE_POSTS = [
  { title: 'Why I’m Building in Public', date: 'Feb 2026' },
  // { title: 'Moving to Rust: A Technical Retrospective', date: 'Sep 2023' },
  // { title: 'Minimalism in Software Design', date: 'Aug 2023' },
  // { title: 'Distributed Consensus for Humans', date: 'Jul 2023' },
];

function BlogPostCard({
  title,
  date,
  dark,
  onClick,
}: {
  title: string;
  date: string;
  dark: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'group flex items-center gap-4 px-4 py-4 justify-between border-b transition-all text-left',
        dark
          ? 'border-slate-800/50 hover:bg-[rgba(84,212,248,0.05)]'
          : 'border-slate-100 hover:bg-slate-100/50'
      )}
      type="button"
    >
      <p
        className={cn(
          'text-base md:text-lg font-normal leading-normal flex-1 truncate transition-colors',
          dark
            ? 'text-white group-hover:text-[var(--primary)]'
            : 'text-slate-800 group-hover:text-[var(--primary)]'
        )}
        style={{ '--primary': PRIMARY } as React.CSSProperties}
      >
        {title}
      </p>
      <div className="shrink-0">
        <p
          className={cn(
            'text-sm font-normal leading-normal mono-text',
            dark ? 'text-[#9cb3ba]' : 'text-slate-400'
          )}
        >
          {date}
        </p>
      </div>
    </button>
  );
}
